import { useEffect, useState } from 'react';
import { fetchJsonCached } from '../lib/settingsCache';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useSiteFeatureFlags = () => {
  const [flags, setFlags] = useState({
    _loaded: false,
    cart_enabled: true,
    quotes_enabled: true,
    pawn_checkout: true,
    storage_online: false,
    storage_pos: false,
    ai_products: true,
    notifications: false,
    sms: false,
    analytics: true,
    require_account_for_checkout: false,
    require_email_verification_for_registration: true,
    left_menu_enabled: true,
    coming_soon_enabled: false,
    coming_soon_password: '8487',
    external_api_enabled: true,
    inventory_enabled: false,
    events_enabled: false,
    events_landing_enabled: false,
    events_center_name: 'Event Center',
    activity_marketplace_enabled: false,
    webcams_enabled: false,
    food_drink_enabled: false,
    hiking_enabled: false,
    beaches_enabled: false,
    weather_enabled: false,
    auto_rentals_enabled: false,
    transportation_enabled: true,
  });

  useEffect(() => {
    let active = true;

    const fetchFlags = async () => {
      try {
        // Fetch both in parallel so a slow/failing site-settings request never
        // blocks (or gates) the feature flags that control the coming-soon gate.
        const [siteRes, ffRes] = await Promise.allSettled([
          fetchJsonCached(`${API_URL}/api/settings/site`),
          fetchJsonCached(`${API_URL}/api/settings/feature-flags`),
        ]);

        const siteData = siteRes.status === 'fulfilled' ? (siteRes.value || {}) : {};
        const featureData = ffRes.status === 'fulfilled' ? (ffRes.value || {}) : {};

        if (!active) return;

        setFlags({
          _loaded: true,
          cart_enabled: featureData.cart_enabled !== false,
          quotes_enabled: featureData.quotes_enabled !== false,
          pawn_checkout: featureData.pawn_checkout !== false,
          storage_online: Boolean(featureData.storage_online),
          storage_pos: Boolean(featureData.storage_pos),
          ai_products: featureData.ai_products !== false,
          notifications: Boolean(featureData.notifications),
          sms: Boolean(featureData.sms),
          analytics: featureData.analytics !== false,
          require_account_for_checkout: Boolean(siteData.require_account_for_checkout),
          require_email_verification_for_registration:
            siteData.require_email_verification_for_registration !== false,
          left_menu_enabled: featureData.left_menu_enabled !== false,
          coming_soon_enabled: featureData.coming_soon_enabled === true,
          coming_soon_password: featureData.coming_soon_password || '8487',
          external_api_enabled: featureData.external_api_enabled !== false,
          inventory_enabled: Boolean(featureData.inventory_enabled),
          events_enabled: Boolean(featureData.events_enabled),
          events_landing_enabled: Boolean(featureData.events_landing_enabled),
          events_center_name: featureData.events_center_name || 'Event Center',
          activity_marketplace_enabled: Boolean(featureData.activity_marketplace_enabled),
          webcams_enabled: Boolean(featureData.webcams_enabled),
          food_drink_enabled: Boolean(featureData.food_drink_enabled),
          hiking_enabled: Boolean(featureData.hiking_enabled),
          beaches_enabled: Boolean(featureData.beaches_enabled),
          weather_enabled: Boolean(featureData.weather_enabled),
          auto_rentals_enabled: Boolean(featureData.auto_rentals_enabled),
          transportation_enabled: featureData.transportation_enabled !== false,
        });
      } catch (error) {
        // On error, fail OPEN (never lock the site behind the gate) but mark loaded.
        if (active) {
          setFlags((prev) => ({ ...prev, _loaded: true, coming_soon_enabled: false }));
        }
      }
    };

    fetchFlags();

    return () => {
      active = false;
    };
  }, []);

  return flags;
};
